import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError, SearchParams } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  SalaryGradeCreateSchema,
  SalaryGradeQueryAllSchema,
  SalaryGradeQuerySchema,
  SalaryGradeUpdateSchema,
} from './schemas';

export function useGetSalaryGradesControlled({ per_page }: SearchParams<{ per_page: number }>, enabled = true) {
  return useQuery({
    queryKey: ['SALARY_GRADES', per_page],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<SalaryGradeQueryAllSchema>({
        endpoint: '/api/v1/salary-grades',
        params: { per_page },
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['SALARY_GRADES'] });
};

export const useGetSalaryGradesMutation: UseControlledMutation<SalaryGradeQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['SALARY_GRADES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<SalaryGradeQueryAllSchema>({
            endpoint: '/api/v1/salary-grades',
            params: { search, per_page },
          });
          return res?.data || [];
        },
      });
    },
  });

export function useGetSalaryGrades(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['SALARY_GRADES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<SalaryGradeQueryAllSchema>({
        endpoint: '/api/v1/salary-grades',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, SalaryGradeCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/salary-grades/',
        payload,
      }),
  });

export function useGetSalaryGrade(id: string) {
  return useSuspenseQuery({
    queryKey: ['SALARY_GRADE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: SalaryGradeQuerySchema }>({
        endpoint: `/api/v1/salary-grades/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: SalaryGradeQuerySchema }, ApiError, Partial<SalaryGradeUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/salary-grades/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['SALARY_GRADE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/salary-grades/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
